"use client"

import { PreferredLanguage } from "@/models/enum"
import { createContext, useContext, useState, useEffect, ReactNode } from "react"

interface LanguageContextType {
  language: PreferredLanguage
  setLanguage: (lang: PreferredLanguage) => void
}

const LanguageContext = createContext<LanguageContextType | null>(null)

const LANGUAGE_STORAGE_KEY = "notification-center-language"

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<PreferredLanguage>(PreferredLanguage.English)

  // Load saved language from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) as PreferredLanguage
      if (savedLanguage && (savedLanguage === PreferredLanguage.English || savedLanguage === PreferredLanguage.French)) {
        setLanguageState(savedLanguage)
      }
    }
  }, [])

  // Save language to localStorage when it changes
  const setLanguage = (lang: PreferredLanguage) => {
    setLanguageState(lang)
    if (typeof window !== "undefined") {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
    }
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
} 